import type { CardAnswer, ReadAnswer, ReadRow } from "../app/reads.js";
import type { ScheduleRow } from "../app/mandates-model.js";
import { el } from "../lib/dom.js";
import { icon } from "../lib/icon.js";
import { scheduleRow } from "./scheduled.js";

/**
 * 10 read-only answers (UX spec v3 §6.11): `.bank.read` — what it is (`.lead`), when the bank read it
 * (`.asof`) — and never a sign button or a primary button. The only control a read card carries is the
 * customer's own reveal on card details (a ghost), and each value's copy button.
 * DCTA-124 (Umakhan's ruling, 5 Oct): no Bank tag — the Bank surface is only the card the customer signs
 * and its result. The figures keep their card (they are the bank's read, not the assistant's words);
 * whether that frame stays is the ux-designer's call (journal).
 */
function header(lead: string, asof?: string, status?: string): HTMLElement {
  return el("div", { class: "bh" }, [
    el("b", { class: "lead" }, [lead]),
    ...(asof === undefined ? [] : [el("span", { class: "asof" }, [asof])]),
    ...(status === undefined ? [] : [el("span", { class: "status" }, [status])]),
  ]);
}

function shell(lead: string, parts: readonly HTMLElement[], status?: string): HTMLElement {
  return el("div", { class: "bank read enter", role: "group", aria: { label: `${lead}${status === undefined ? "" : ` · ${status.toLowerCase()}`} · nothing to sign` } }, [
    ...parts,
  ]);
}

function item(r: ReadRow): HTMLElement {
  return el("div", { class: "item" }, [
    el("span", { class: "ic", aria: { hidden: "true" } }, [r.ic === undefined ? (r.initial ?? "") : icon(r.ic)]),
    el("span", { class: "tx" }, [el("b", {}, [r.title]), ...(r.sub === undefined ? [] : [el("small", {}, [r.sub])])]),
    el("span", { class: "amt money" }, [r.amount, ...(r.small === undefined ? [] : [el("small", {}, [r.small])])]),
  ]);
}

export function readCard(a: ReadAnswer): HTMLElement {
  const rows: HTMLElement[] = a.rows.map(item);
  if (a.total !== undefined) {
    rows.push(el("div", { class: "item total-row" }, [el("span", { class: "tx" }, [el("b", {}, [a.total.label])]), el("span", { class: "amt money" }, [a.total.amount])]));
  }
  return shell(a.lead, [
    header(a.lead, a.asof),
    ...(a.stat === undefined ? [] : [el("div", { class: "stat" }, [el("b", { class: "money" }, [a.stat.figure]), el("small", {}, [a.stat.sub])])]),
    ...(rows.length === 0 ? [] : [el("div", { class: "rows" }, [el("div", { class: "list" }, rows)])]),
    ...(a.kv === undefined || a.kv.length === 0
      ? []
      : [el("div", { class: "rows" }, [el("div", { class: "kv" }, a.kv.map((r) => el("div", {}, [el("span", { class: "k" }, [r.k]), el("span", { class: "v money" }, [r.v])])))])]),
  ]);
}


/**
 * DCTA-209 (F61, 09-scheduled.md row 29b, mock-up `read` · `next`): what is scheduled, as the bank's read answers it — "Scheduled"
 * (or "Scheduled to {name}" when the read names a destination), when the bank read it, a row per schedule (each opens the
 * schedule), and "See all in Activity" under the unfiltered read. No Bank tag, nothing to sign. An empty read draws no card:
 * the bank's line says so alone.
 */
export function scheduledReadCard(
  rows: readonly ScheduleRow[],
  head: { readonly lead: string; readonly asof?: string },
  h: { readonly onOpen: (mandateId: string) => void; readonly onSeeAll?: () => void },
): HTMLElement {
  let foot: HTMLElement | null = null;
  if (h.onSeeAll !== undefined) {
    const all = el("button", { class: "raw btn link openact", type: "button" }, ["See all in Activity"]);
    all.addEventListener("click", h.onSeeAll);
    foot = el("div", { class: "bf" }, [all]);
  }
  return shell(head.lead, [
    header(head.lead, head.asof),
    el("div", { class: "rows feed", role: "list", aria: { label: head.lead } }, rows.map((r) => scheduleRow(r, h.onOpen))),
    ...(foot === null ? [] : [foot]),
  ]);
}

export interface CardDetailsHandlers {
  /** The bank's reveal (POST, no-store): the values and how long they may stay. */
  reveal(): Promise<{ readonly pan: string; readonly expiry: string; readonly cvv: string; readonly visible_seconds: number }>;
  shown(): void;
  hidden(): void;
}

const FIELDS = [
  { k: "Number", copy: "card number" },
  { k: "Expiry", copy: "expiry" },
  { k: "Security code", copy: "security code" },
] as const;

/**
 * Card details (§6.11 card-details-hidden / -shown, decision 31): masked until the
 * customer's tap; the bank's values then, with a copy button each; hidden again on
 * their tap, on leaving the chat, or when the bank's own window ends (measured from
 * arrival, like the card's TTL). The auto-hide line is a static sentence — nothing
 * on a security surface counts down. Nothing here is ever read aloud.
 */
export class CardDetails {
  readonly element: HTMLElement;
  readonly #a: CardAnswer;
  readonly #h: CardDetailsHandlers;
  readonly #kv: HTMLElement;
  readonly #toggle: HTMLButtonElement;
  readonly #hint: HTMLElement;
  readonly #live: HTMLElement;
  #timer: ReturnType<typeof setTimeout> | undefined;
  #busy = false;
  #shown = false;

  constructor(a: CardAnswer, h: CardDetailsHandlers) {
    this.#a = a;
    this.#h = h;
    this.#kv = el("div", { class: "kv" });
    this.#toggle = el("button", { class: "raw btn ghost", type: "button", aria: { busy: "false" } });
    this.#toggle.addEventListener("click", () => void (this.#shown ? this.hide() : this.#reveal()));
    this.#hint = el("div", { class: "hint" });
    this.#live = el("span", { class: "vis-hidden", aria: { live: "polite" } });
    this.element = shell(a.lead, [
      header(a.lead),
      el("div", { class: "rows" }, [this.#kv]),
      el("div", { class: "bf" }, [el("div", { class: "acts" }, [this.#toggle]), this.#hint]),
      this.#live,
    ]);
    this.#masked();
  }

  get shown(): boolean {
    return this.#shown;
  }

  hide(): void {
    if (this.#timer !== undefined) clearTimeout(this.#timer);
    this.#timer = undefined;
    const was = this.#shown;
    this.#masked();
    if (was) this.#h.hidden();
  }

  #masked(): void {
    this.#shown = false;
    const m = this.#a.masked;
    this.#rows([m.number, m.expiry, m.cvv], true);
    this.#toggle.replaceChildren(icon("i-eye"), "Show details");
    this.#hint.textContent = "Only you can show them. They hide again after 30 seconds.";
  }

  async #reveal(): Promise<void> {
    if (this.#busy) return;
    this.#busy = true;
    this.#toggle.setAttribute("aria-busy", "true");
    try {
      const d = await this.#h.reveal();
      this.#shown = true;
      this.#rows([d.pan, d.expiry, d.cvv], false);
      this.#toggle.replaceChildren(icon("i-eye-off"), "Hide details");
      this.#hint.textContent = "Hides on its own in 30 seconds.";
      this.#timer = setTimeout(() => this.hide(), d.visible_seconds * 1000);
      this.#h.shown();
    } catch {
      // Nothing was shown; the mask stays and the customer can tap again.
    } finally {
      this.#busy = false;
      this.#toggle.setAttribute("aria-busy", "false");
    }
  }

  #rows(values: readonly string[], masked: boolean): void {
    this.#kv.replaceChildren(
      ...FIELDS.map((f, i) => {
        const value = values[i] ?? "";
        const parts: HTMLElement[] = [el("span", { class: "k" }, [f.k]), el("span", { class: masked ? "v secret money masked" : "v secret money" }, [value])];
        if (!masked) {
          const copy = el("button", { class: "raw copy", type: "button", aria: { label: `Copy ${f.copy}` } }, [icon("i-copy")]);
          copy.addEventListener("click", () => {
            void navigator.clipboard?.writeText(value).catch(() => undefined);
            copy.dataset["done"] = "true";
            copy.replaceChildren(icon("i-check"));
            this.#live.textContent = `Copied ${f.copy}`;
            setTimeout(() => {
              copy.dataset["done"] = "false";
              copy.replaceChildren(icon("i-copy"));
            }, 1500);
          });
          parts.push(copy);
        }
        return el("div", { class: "sv" }, parts);
      }),
    );
  }
}
